import { useCallback, useMemo } from "react";
import { SxProps, Theme } from "@mui/material";
import {
  DataGrid,
  DataGridProps,
  GridCellParams,
  GridColDef,
  GridDensity,
  GridPaginationModel,
  GridRowClassNameParams,
  GridRowId,
  GridRowParams,
  GridValidRowModel,
} from "@mui/x-data-grid";
import { EDITABLE_GRID_PAGE_SIZE_OPTIONS, cellHighlightClassName, editableGridCellSx } from "./gridHelpers";

export interface IEditableDataGridProps<TRow extends GridValidRowModel> {
  rows: TRow[];
  columns: GridColDef<TRow>[];
  getRowId: (row: TRow) => GridRowId;
  /** Called with the full row list after an inline edit is committed. */
  onRowsChange: (rows: TRow[]) => void;
  /** Adjust a committed row before it is stored and displayed; return the row to keep. */
  normalizeRow?: (newRow: TRow, oldRow: TRow) => TRow;
  /** Marks a cell with the dirty class when it returns true. */
  isCellDirty?: (row: TRow, field: string) => boolean;
  /** Marks a cell with the invalid class when it returns true. Wins over dirty. */
  isCellInvalid?: (row: TRow, field: string) => boolean;
  /** Cell-level editability gate; a column must also be `editable` for the cell to open. */
  isCellEditable?: (row: TRow, field: string) => boolean;
  /** Controlled pagination. Omit to let the grid page on its own. */
  paginationModel?: GridPaginationModel;
  onPaginationModelChange?: (model: GridPaginationModel) => void;
  /** Defaults to EDITABLE_GRID_PAGE_SIZE_OPTIONS. */
  pageSizeOptions?: number[];
  /** Merged over the shared dirty/invalid cell styles. */
  sx?: SxProps<Theme>;
  /** Defaults to "compact". */
  density?: GridDensity;
  loading?: boolean;
  disableRowSelectionOnClick?: boolean;
  autoHeight?: boolean;
  /** Applied to the grid root element. */
  "data-testid"?: string;
  /** Applied as `data-testid` to every rendered row element. */
  rowTestId?: string;
  /** Called with the row that was clicked. */
  onRowClick?: (row: TRow) => void;
  /** A class to add to a row's element, for example to mark the selected row. */
  getRowClassName?: (row: TRow) => string;
}

/**
 * Editable community DataGrid in cell edit mode. Edits are committed through
 * `processRowUpdate` and reported as a full row list via `onRowsChange`. Dirty and
 * invalid cells are highlighted through the shared cell classes.
 */
export const EditableDataGrid = <TRow extends GridValidRowModel>(props: IEditableDataGridProps<TRow>): JSX.Element => {
  const {
    rows,
    columns,
    getRowId,
    onRowsChange,
    normalizeRow,
    isCellDirty,
    isCellInvalid,
    isCellEditable,
    paginationModel,
    onPaginationModelChange,
    pageSizeOptions = EDITABLE_GRID_PAGE_SIZE_OPTIONS,
    sx,
    density = "compact",
    loading,
    disableRowSelectionOnClick,
    autoHeight,
    "data-testid": dataTestId,
    rowTestId,
    onRowClick,
    getRowClassName,
  } = props;

  const handleProcessRowUpdate = useCallback(
    (newRow: TRow, oldRow: TRow): TRow => {
      const updated = normalizeRow ? normalizeRow(newRow, oldRow) : newRow;
      const updatedId = getRowId(updated);
      onRowsChange(rows.map((r) => (getRowId(r) === updatedId ? updated : r)));
      return updated;
    },
    [rows, onRowsChange, normalizeRow, getRowId]
  );

  const getCellClassName = useCallback(
    (params: GridCellParams<TRow>): string =>
      cellHighlightClassName(
        isCellDirty ? isCellDirty(params.row, params.field) : false,
        isCellInvalid ? isCellInvalid(params.row, params.field) : false
      ),
    [isCellDirty, isCellInvalid]
  );

  const handleIsCellEditable = useCallback(
    (params: GridCellParams<TRow>): boolean => (isCellEditable ? isCellEditable(params.row, params.field) : true),
    [isCellEditable]
  );

  const handleRowClick = useCallback((params: GridRowParams<TRow>): void => onRowClick?.(params.row), [onRowClick]);

  const handleRowClassName = useCallback(
    (params: GridRowClassNameParams<TRow>): string => getRowClassName?.(params.row) ?? "",
    [getRowClassName]
  );

  // The grid's own SxProps type comes from a different @mui/system copy than @mui/material's,
  // so the merged array is typed through DataGridProps.
  const mergedSx = useMemo<DataGridProps["sx"]>(
    () => [editableGridCellSx, ...(Array.isArray(sx) ? sx : [sx])].filter(Boolean) as DataGridProps["sx"],
    [sx]
  );

  const slotProps = useMemo(() => (rowTestId ? { row: { "data-testid": rowTestId } } : undefined), [rowTestId]);

  return (
    <DataGrid<TRow>
      data-testid={dataTestId}
      getRowId={getRowId}
      rows={rows}
      columns={columns}
      density={density}
      editMode="cell"
      processRowUpdate={handleProcessRowUpdate}
      getCellClassName={isCellDirty || isCellInvalid ? getCellClassName : undefined}
      isCellEditable={isCellEditable ? handleIsCellEditable : undefined}
      sx={mergedSx}
      loading={loading}
      disableRowSelectionOnClick={disableRowSelectionOnClick}
      autoHeight={autoHeight}
      slotProps={slotProps}
      onRowClick={onRowClick ? handleRowClick : undefined}
      getRowClassName={getRowClassName ? handleRowClassName : undefined}
      pagination
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      pageSizeOptions={pageSizeOptions}
    />
  );
};

export default EditableDataGrid;
